import { jsPDF } from "jspdf";
import dayjs from "dayjs";
import { MdTextRender } from "jspdf-md-renderer";
import { fontManager } from "../font/font-manager";
import { isTableLine, isTableSeparator, parseTableRows } from "../parser/markdown-parser";
import { parseBoldSegments } from "../parser/text-parser";
import {
  PDF_MARGIN,
  PDF_PAGE_WIDTH,
  PDF_MAX_CONTENT_Y,
  PDF_TITLE_FONT_SIZE,
  PDF_SUBTITLE_FONT_SIZE,
  PDF_TABLE_FONT_SIZE,
  PDF_BODY_TEXT_COLOR,
  getLineHeight
} from "../constants";
import type { ExportReportArguments } from "../types";
import { calculateCellHeight, renderCell } from "./pdf-utils";

/** 마크다운 콘텐츠를 PDF로 변환하여 다운로드 */
export const exportPdf = async ({
  content,
  references = [],
  fileName,
  createdAt,
}: ExportReportArguments): Promise<void> => {
  const pdf = new jsPDF("p", "mm", "a4");
  // 콘텐츠를 분석하여 필요한 폰트를 동적으로 로드
  const fontLoaded = await fontManager.setupPdf(pdf, content);

  let y = PDF_MARGIN;

  /** 공간 부족 시 새 페이지 추가 */
  const ensurePageSpace = (needed: number): void => {
    if (y + needed > PDF_MAX_CONTENT_Y) {
      pdf.addPage();
      y = PDF_MARGIN + 5;
    }
  };

  /** 테이블 렌더링 */
  const addTable = (rows: string[][]): void => {
    const tableWidth = PDF_PAGE_WIDTH - PDF_MARGIN * 2;
    const colCount = rows[0]?.length ?? 0;

    // 컬럼 너비 비율 (각 열별 가중치 설정)
    const getColumnWeight = (colIndex: number): number => {
      if (colIndex === 0) return 1;      // 첫 번째 열
      if (colIndex === 1) return 1.5;    // 두 번째 열 (더 넓게)
      if (colIndex === 2) return 1.2;    // 세 번째 열
      return 0.8;                         // 나머지 열
    };

    // 전체 가중치 합계 계산
    const totalWeight = Array.from({ length: colCount }, (_, i) => getColumnWeight(i))
      .reduce((sum, weight) => sum + weight, 0);

    // 각 열의 실제 너비 계산
    const colWidths = Array.from({ length: colCount }, (_, i) => 
      (tableWidth * getColumnWeight(i)) / totalWeight
    );

    // 각 열의 시작 X 좌표 미리 계산 (겹침 방지)
    const colStartX = colWidths.map((_, colIndex) => 
      PDF_MARGIN + colWidths.slice(0, colIndex).reduce((a, b) => a + b, 0)
    );

    const cellLineHeight = getLineHeight(PDF_TABLE_FONT_SIZE);
    const cellPad = 2;
    const rowVerticalPadding = 2;
    const rowLineMarginAbove = 5;  // 행-가로선 간 여백
    const rowLineMarginBelow = 5;

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      const isHeader = rowIndex === 0;

      if (rowIndex === 1) {
        y += 2;
      }

      const cellHeights = row.map((cell, colIndex) =>
        calculateCellHeight(cell, colWidths[colIndex], cellPad, isHeader, pdf, fontLoaded)
      );

      const maxRowHeight = Math.max(...cellHeights, cellLineHeight);

      ensurePageSpace(maxRowHeight + rowVerticalPadding * 2 + rowLineMarginAbove + rowLineMarginBelow);

      const rowStartY = y + rowVerticalPadding;

      let actualRowHeight = 0;
      row.forEach((cell, colIndex) => {
        // 미리 계산된 열 시작 좌표 사용 (겹침 방지)
        const cellX = colStartX[colIndex] + cellPad;
        const cellBottomY = renderCell(cell, colWidths[colIndex], cellPad, cellX, rowStartY, isHeader, pdf, fontLoaded);
        const cellContentHeight = cellBottomY - rowStartY;
        if (cellContentHeight > actualRowHeight) {
          actualRowHeight = cellContentHeight;
        }
      });

      const usedRowHeight = Math.max(maxRowHeight, actualRowHeight);
      y += usedRowHeight + rowVerticalPadding * 2 + rowLineMarginAbove + rowLineMarginBelow;

      // 행 구분선 (페이지 넘김 시 새 페이지 첫 행 위에는 미표시)
      if (rowIndex < rows.length - 1) {
        const rowBottomY = rowStartY + usedRowHeight + rowLineMarginAbove;
        if (rowBottomY <= PDF_MAX_CONTENT_Y) {
          pdf.setDrawColor(180, 180, 180);
          pdf.setLineWidth(0.2);
          pdf.line(PDF_MARGIN, rowBottomY, PDF_MARGIN + tableWidth, rowBottomY);
        }
      }
    }

    y += cellLineHeight;
  };

  // 타이틀
  const h1Match = content.match(/^#\s+(.+)$/m);
  if (h1Match) {
    y += 20;
    const titleText = h1Match[1];
    fontManager.setFont(pdf, true, fontLoaded, titleText);
    pdf.setFontSize(PDF_TITLE_FONT_SIZE);

    // A4 용지 너비에 맞게 제목 줄바꿈 처리
    const maxTitleWidth = PDF_PAGE_WIDTH - PDF_MARGIN * 2;
    const titleLines = pdf.splitTextToSize(titleText, maxTitleWidth);

    // 각 줄마다 렌더링
    titleLines.forEach((line: string, index: number) => {
      if (index > 0) {
        y += getLineHeight(PDF_TITLE_FONT_SIZE);
        ensurePageSpace(getLineHeight(PDF_TITLE_FONT_SIZE));
      }
      pdf.text(line, PDF_MARGIN, y);
    });

    y += getLineHeight(PDF_TITLE_FONT_SIZE) + 3;
  }

  // 날짜
  if (createdAt) {
    ensurePageSpace(getLineHeight(PDF_SUBTITLE_FONT_SIZE));
    fontManager.setFont(pdf, false, fontLoaded);
    pdf.setFontSize(PDF_SUBTITLE_FONT_SIZE);
    pdf.setTextColor(128, 128, 128);
    pdf.text(dayjs(createdAt).format("YYYY.MM.DD"), PDF_MARGIN, y);
    pdf.setTextColor(0, 0, 0);
    y += getLineHeight(PDF_SUBTITLE_FONT_SIZE);
  }

  // 본문 렌더 옵션
  const lines = content.split("\n");
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PDF_MARGIN * 2;
  const contentHeight = pageHeight - PDF_MARGIN * 2;
  const bodyFontSize = 10.5;

  const renderOptions = {
    cursor: { x: PDF_MARGIN, y },
    page: {
      format: "a4",
      unit: "mm",
      orientation: "portrait",
      maxContentWidth: contentWidth,
      maxContentHeight: contentHeight,
      lineSpace: 1.5,
      defaultLineHeightFactor: 1.2,
      defaultFontSize: bodyFontSize,
      defaultTitleFontSize: 15,
      topmargin: PDF_MARGIN,
      xpading: PDF_MARGIN,
      xmargin: PDF_MARGIN,
      indent: PDF_MARGIN,
    },
    font: {
      bold: { name: fontLoaded ? "Pretendard" : "helvetica", style: "bold" },
      regular: { name: fontLoaded ? "Pretendard" : "helvetica", style: "normal" },
      light: { name: fontLoaded ? "Pretendard" : "helvetica", style: "normal" },
    },
    endCursorYHandler: (newY: number) => {
      y = newY;
    },
  };

  /** H2~H5 텍스트 추출 */
  const parseHeadingText = (line: string): string | null => {
    const match = line.match(/^#{2,5}\s+(.+)$/);
    return match ? match[1] : null;
  };

  /** bold/regular 세그먼트 너비 합산 */
  const calculateSegmentsWidth = (segments: { text: string; bold: boolean }[]): number => {
    let totalWidth = 0;
    for (const seg of segments) {
      fontManager.setFont(pdf, seg.bold, fontLoaded, seg.text);
      pdf.setFontSize(bodyFontSize);
      totalWidth += pdf.getTextWidth(seg.text);
    }
    return totalWidth;
  };

  /** **bold** 파싱 후 렌더링, 반환값은 다음 Y */
  const renderTextWithBoldSegments = (text: string, startX: number, startY: number, maxWidth: number): number => {
    pdf.setTextColor(...PDF_BODY_TEXT_COLOR);
    fontManager.setFont(pdf, false, fontLoaded, text);
    pdf.setFontSize(bodyFontSize);

    const segments = parseBoldSegments(text);
    const lineHeight = getLineHeight(bodyFontSize);
    let currentY = startY;
    let currentSegments: { text: string; bold: boolean }[] = [];

    for (const seg of segments) {
      const words = seg.text.split(" ");

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const spaceAfter = i < words.length - 1 ? " " : "";
        const wordWithSpace = word + spaceAfter;

        const testSegments = [...currentSegments, { text: wordWithSpace, bold: seg.bold }];
        const testWidth = calculateSegmentsWidth(testSegments);

        if (testWidth <= maxWidth) {
          currentSegments.push({ text: wordWithSpace, bold: seg.bold });
        } else {
          if (currentSegments.length > 0) {
            y = currentY;
            ensurePageSpace(lineHeight);
            currentY = y;

            let curX = startX;
            for (const cseg of currentSegments) {
              fontManager.setFont(pdf, cseg.bold, fontLoaded, cseg.text);
              pdf.setFontSize(bodyFontSize);
              pdf.setTextColor(...PDF_BODY_TEXT_COLOR);
              const segWidth = pdf.getTextWidth(cseg.text);
              pdf.text(cseg.text, curX, currentY);
              curX += segWidth;
            }
            currentY += lineHeight;
          }
          currentSegments = [{ text: wordWithSpace, bold: seg.bold }];
        }
      }
    }

    if (currentSegments.length > 0) {
      y = currentY;
      ensurePageSpace(lineHeight);
      currentY = y;

      let curX = startX;
      for (const cseg of currentSegments) {
        fontManager.setFont(pdf, cseg.bold, fontLoaded, cseg.text);
        pdf.setFontSize(bodyFontSize);
        pdf.setTextColor(...PDF_BODY_TEXT_COLOR);
        const segWidth = pdf.getTextWidth(cseg.text);
        pdf.text(cseg.text, curX, currentY);
        curX += segWidth;
      }
      currentY += lineHeight;
    }

    return currentY;
  };

  /** 마크다운 블록 렌더 (리스트/헤딩/코드/일반문 구분) */
  const renderMarkdownWithBoldColor = async (markdown: string): Promise<void> => {
    pdf.setTextColor(...PDF_BODY_TEXT_COLOR);
    const markdownLines = markdown.trim().split("\n");
    let currentY = y;

    for (const line of markdownLines) {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        currentY += getLineHeight(bodyFontSize) * 0.7;  // 빈 줄
        continue;
      }

      const isListItem = (trimmedLine.startsWith("- ") || trimmedLine.startsWith("* ")) &&
                         !trimmedLine.startsWith("**");
      const isComplexMarkdown =
        trimmedLine.startsWith("#") ||
        trimmedLine.startsWith("```") ||
        trimmedLine.startsWith(">");

      if (isListItem) {
        const bulletChar = "• ";  // - 또는 * 리스트
        const listContent = trimmedLine.substring(2);
        const bulletIndent = 3;
        const textIndent = bulletIndent + pdf.getTextWidth(bulletChar);

        y = currentY;
        ensurePageSpace(getLineHeight(bodyFontSize));
        currentY = y;
        fontManager.setFont(pdf, false, fontLoaded, listContent);
        pdf.setFontSize(bodyFontSize);
        pdf.setTextColor(...PDF_BODY_TEXT_COLOR);
        pdf.text(bulletChar, PDF_MARGIN + bulletIndent, currentY);
        currentY = renderTextWithBoldSegments(listContent, PDF_MARGIN + textIndent, currentY, contentWidth - textIndent);
      } else if (isComplexMarkdown) {
        // 헤딩, 코드블록, 인용문 (MdTextRender)
        y = currentY;
        renderOptions.cursor.y = y;
        await MdTextRender(pdf, trimmedLine + "\n", renderOptions);
        const lineCount = Math.ceil(trimmedLine.length / 80) || 1;
        const estimatedHeight = getLineHeight(bodyFontSize) * lineCount;
        currentY = Math.max(renderOptions.cursor.y, y + estimatedHeight);
      } else {
        // 일반 텍스트
        y = currentY;
        currentY = renderTextWithBoldSegments(trimmedLine, PDF_MARGIN, currentY, contentWidth);
      }
    }

    y = currentY;
    pdf.setTextColor(...PDF_BODY_TEXT_COLOR);
  };

  let currentMarkdown = "";
  let i = 0;

  pdf.setTextColor(...PDF_BODY_TEXT_COLOR);

  // 본문 순회: 헤딩/테이블/마크다운 분기
  while (i < lines.length) {
    const trimmedLine = lines[i].trim();

    if (trimmedLine.match(/^#\s+/)) {
      i++;
      continue;
    }

    const headingText = parseHeadingText(trimmedLine);
    if (headingText) {
      if (currentMarkdown.trim()) {
        try {
          renderOptions.cursor.y = y;
          await renderMarkdownWithBoldColor(currentMarkdown);
        } catch (error) {
          console.error("PDF 마크다운 렌더링 오류:", error);
        }
        currentMarkdown = "";
      }

      const headingFontSize = 15;
      ensurePageSpace(getLineHeight(headingFontSize));
      y += 10;

      fontManager.setFont(pdf, true, fontLoaded, headingText);
      pdf.setFontSize(headingFontSize);
      pdf.setTextColor(0, 0, 0);

      // 서브 헤딩도 A4 용지 너비에 맞게 줄바꿈 처리
      const maxHeadingWidth = PDF_PAGE_WIDTH - PDF_MARGIN * 2;
      const headingLines = pdf.splitTextToSize(headingText, maxHeadingWidth);

      headingLines.forEach((line: string, index: number) => {
        if (index > 0) {
          y += getLineHeight(headingFontSize);
          ensurePageSpace(getLineHeight(headingFontSize));
        }
        pdf.text(line, PDF_MARGIN, y);
      });

      y += getLineHeight(headingFontSize) + 2;

      i++;
      continue;
    }

    if (isTableLine(trimmedLine)) {
      // 테이블 파싱 및 렌더
      if (currentMarkdown.trim()) {
        try {
          renderOptions.cursor.y = y;
          await renderMarkdownWithBoldColor(currentMarkdown);
        } catch (error) {
          console.error("PDF 마크다운 렌더링 오류:", error);
        }
        currentMarkdown = "";
      }

      const tableLines: string[] = [];
      const startIndex = i;

      while (i < lines.length && isTableLine(lines[i].trim())) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length >= 2 && isTableSeparator(tableLines[1])) {
        addTable(parseTableRows(tableLines));
        continue;
      }

      i = startIndex;
    }

    currentMarkdown += lines[i] + "\n";
    i++;
  }

  if (currentMarkdown.trim()) {
    try {
      renderOptions.cursor.y = y;
      await renderMarkdownWithBoldColor(currentMarkdown);
    } catch (error) {
      console.error("PDF 마크다운 렌더링 오류:", error);
    }
  }

  pdf.setTextColor(0, 0, 0);

  // 참고문헌
  if ((references?.length ?? 0) > 0) {
    y += 5;
    ensurePageSpace(getLineHeight(PDF_TITLE_FONT_SIZE) * (4 / 3) + 5);

    y += getLineHeight(PDF_TITLE_FONT_SIZE) / 3;
    fontManager.setFont(pdf, true, fontLoaded);
    pdf.setFontSize(PDF_TITLE_FONT_SIZE);
    pdf.text("References", PDF_MARGIN, y);
    y += getLineHeight(PDF_TITLE_FONT_SIZE) + 5;

    const refLineHeight = getLineHeight(PDF_TABLE_FONT_SIZE - 1);
    const refItemMargin = 4;  // 참고문헌 항목 간 여백
    const maxRefWidth = PDF_PAGE_WIDTH - PDF_MARGIN * 2;

    references.forEach((ref, index) => {
      const linkText = `${index + 1}. ${ref.title}`;
      fontManager.setFont(pdf, false, fontLoaded, ref.title);
      pdf.setFontSize(PDF_TABLE_FONT_SIZE - 1);
      pdf.setTextColor(...PDF_BODY_TEXT_COLOR);

      // 참고문헌 제목도 A4 용지 너비에 맞게 줄바꿈 처리
      const refLines = pdf.splitTextToSize(linkText, maxRefWidth);
      const totalRefHeight = refLines.length * refLineHeight + refItemMargin;

      ensurePageSpace(totalRefHeight);

      // 각 줄마다 렌더링 및 링크 추가
      refLines.forEach((line: string, lineIndex: number) => {
        pdf.text(line, PDF_MARGIN, y);

        // 첫 줄에만 링크 추가 (전체 참고문헌에 대한 링크)
        if (lineIndex === 0) {
          const lineWidth = pdf.getTextDimensions(line).w;
          pdf.link(PDF_MARGIN, y - (PDF_TABLE_FONT_SIZE - 1) * 0.7, lineWidth, PDF_TABLE_FONT_SIZE - 1, { url: ref.url });
        }

        y += refLineHeight;
      });

      pdf.setTextColor(0, 0, 0);
      y += refItemMargin;
    });
  }

  pdf.save(`${fileName}.pdf`);
};
